import assertNever from "assert-never";
import React from "react";
import { Page } from "../activities/AppPage";
import { API } from "../API";
import useCaregiver from "../hooks/useCaregiver";
import useMixin from "../hooks/useMixin";
import { isLoading } from "../Loading";
import { OnboardingVisitId, OnboardingVisitInstanceId, PatientId } from "../messages/ObjectIds";
import { AppState } from "../system/AppState";
import { AgencyMember } from "../system/AppState/AppAgenciesState";
import { AppCaregiver } from "../system/AppState/AppCaregiverState";
import { Visit } from "../system/AppState/AppVisitsState";
import { AppChatState } from "../system/AppState/ChatState";
import { doNothing } from "../utils/doNothing";
import AvailableVisitScreen from "./AvailableVisitScreen";
import FinishOnboardingBeforeStaffingModal from "./FinishOnboardingBeforeStaffingModal";
import AppModal from "./Generic/AppModal/AppModal";
import { isDefined } from "./Generic/IsDefined";
import LoadingScaffold from "./LoadingScaffold";
import DirectionsBottomSheet from "./Patient/components/DirectionsBottomSheet";
import useDirections from "./useDirections";
import useOnboardingVisitEngagement from "./useOnboardingVisitEngagement";
import { useVisitChatIcon } from "./VisitChatIcon";
import WithOnboardingMultiShift from "./WithOnboardingMultishift";
import { AgenciesBlock, convertToAgenciesBlocks } from "./AgenciesList/AgenciesListUtils";
import { List } from "../utils/List";
import { AddressComponents } from "../messages";

interface Props {
  appState: AppState;
  api: API;
  onboardingVisitId: OnboardingVisitId;
  onClickChatWithAgency: (patientId: PatientId) => void;
  onRequestNavigate: (page: Page) => void;
  onClickGoBack: () => void;
}

type OnboardingVisitState =
  | {
      type: "Loading";
    }
  | {
      type: "Ready";
      caregiver: AppCaregiver;
      visit: Visit.Onboarding;
      coordinator: AgencyMember;
      countNewPatientChatMessages: number;
      activeAgencies: List<AgenciesBlock>;
    }
  | {
      type: "Error";
    };

export type CheckedOnboardingVisitData =
  | CheckedSinglesOnboardingVisitData
  | CheckedAllShiftsOnboardingVisitData
  | CheckedPatternOnboardingVisitData;

export interface CheckedSinglesOnboardingVisitData {
  type: "Singles";
  visitInstanceId: OnboardingVisitInstanceId;
}
export interface CheckedPatternOnboardingVisitData {
  type: "Pattern";
  dayOfWeek: string;
}

export interface CheckedAllShiftsOnboardingVisitData {
  type: "AllShifts";
}

const OnboardingVisitActivity = (props: Props) => {
  const {
    state: { isPopperOpen },
  } = useVisitChatIcon();

  const mixin = useMixin(props.appState);
  const caregiver = useCaregiver(props.appState);

  const { openLocation, closeLocation, directionsState } = useDirections();

  const {
    engagementModalState,
    promptRequestVisit,
    promptRejectVisit,
    closeEngagementModal,
    promptCallAgency,
    onboardingModalProps,
  } = useOnboardingVisitEngagement({
    visitId: props.onboardingVisitId,
    api: props.api,
    onRequestNavigate: props.onRequestNavigate,
  });

  const handleClickLocation = (location: AddressComponents) => {
    openLocation([location.location.lat, location.location.lng]);
  };

  const handleClickCall = () => {
    promptCallAgency();
  };

  const handleClickRequest = () => {
    promptRequestVisit();
  };

  const handleClickReject = promptRejectVisit;

  const handleClickCancel = () => {
    promptRejectVisit();
  };

  const state = ((): OnboardingVisitState => {
    if (
      isLoading(caregiver) ||
      isLoading(mixin.appVisitsState) ||
      isLoading(mixin.appAgenciesState)
    ) {
      return { type: "Loading" };
    }

    const visit = mixin.appVisitsState.visits.find((x) => x.id === props.onboardingVisitId);
    if (!isDefined(visit) || visit.type !== "Onboarding") {
      console.log(
        `onboarding visit id ${props.onboardingVisitId} could not be found. redirecting back.`
      );
      return { type: "Error" };
    }

    const coordinator = mixin.appAgenciesState.allAgencyMembers.get(
      visit.publicVisitBroadcastDetails.createdById
    );

    if (!isDefined(coordinator)) {
      console.log(
        `coordinator of visit broadcast id ${props.onboardingVisitId} could not be found. redirecting back.`
      );
      return { type: "Error" };
    }

    const countNewPatientChatMessages = AppChatState.patientNumNewChatMessages(
      mixin.appChatState,
      visit.publicVisitBroadcastDetails.patientId
    );

    const activeAgencies = convertToAgenciesBlocks(mixin.appAgenciesState.agencies).filter(
      (x) => x.status === "ACTIVE"
    );

    return {
      type: "Ready",
      caregiver: caregiver,
      visit: visit,
      coordinator: coordinator,
      countNewPatientChatMessages: countNewPatientChatMessages,
      activeAgencies: activeAgencies,
    };
  })();

  switch (state.type) {
    case "Loading":
      return <LoadingScaffold onClickGoBack={props.onClickGoBack} />;
    case "Ready":
      return (
        <>
          <WithOnboardingMultiShift
            visit={state.visit}
            onClickCancel={handleClickCancel}
            onClickRequest={handleClickRequest}
          >
            {(multiShiftProps) => (
              <AvailableVisitScreen
                activeAgencies={state.activeAgencies}
                type="OnboardingVisit"
                {...multiShiftProps}
                visit={state.visit}
                engagementStatus={getVisitEngagementStatus(state.visit)}
                caregiver={state.caregiver}
                coordinator={state.coordinator}
                isPopperOpen={isPopperOpen}
                countNewPatientChatMessages={state.countNewPatientChatMessages}
                onClickLocation={handleClickLocation}
                onClickGoBack={props.onClickGoBack}
                onClickChat={props.onClickChatWithAgency}
                onClickOnboardingVisitCallAgency={handleClickCall}
                onClickReject={handleClickReject}
                onClickRequestFlexible={doNothing}
              />
            )}
          </WithOnboardingMultiShift>
          <DirectionsBottomSheet {...directionsState} onRequestClose={closeLocation} />
          <AppModal {...engagementModalState} onRequestClose={closeEngagementModal} />
          <FinishOnboardingBeforeStaffingModal {...onboardingModalProps} />
        </>
      );
    case "Error":
      return <LoadingScaffold onClickGoBack={props.onClickGoBack} />;
    default:
      assertNever(state);
  }
};

export default OnboardingVisitActivity;

function getVisitEngagementStatus(visit: Visit) {
  if (visit.caregiverVisitEngagementDetails.requested) {
    return "REQUESTED" as const;
  }

  return "AVAILABLE" as const;
}
